import { Balancesheet, Contact, expenseType, ktgType, User, UserSalaryAttributesType } from '@/db/schema'
import {
  AccountType,
  BookingType,
  EntryStatusType,
  ExpenseType,
  ExpenseTypeValue,
  IncomeType,
  KtgValue,
  SalaryType,
  TransferType,
  UserRolesType,
  UserRolesTypeValue,
} from '@/db/types'
import { ResumeSchemaType, ResumesType } from '@/types/resume'
import { clsx, type ClassValue } from 'clsx'
import {
  AlertCircleIcon,
  ArrowDown,
  ArrowDownCircle,
  ArrowUpRightIcon,
  BackpackIcon,
  BanknoteArrowUpIcon,
  Building2Icon,
  CarIcon,
  CheckCircle,
  Circle,
  CircleDashedIcon,
  CircleHelpIcon,
  CircleStarIcon,
  CircleXIcon,
  ClipboardList,
  FilePlusIcon,
  GraduationCapIcon,
  HouseIcon,
  ListTodoIcon,
  MailIcon,
  NotepadTextIcon,
  OctagonAlertIcon,
  PencilIcon,
  PhoneIcon,
  ReceiptSwissFrancIcon,
  ReceiptTextIcon,
  SaladIcon,
  SendIcon,
  Timer,
  UserRoundIcon,
  UsersIcon,
  WalletMinimalIcon,
} from 'lucide-react'
import moment from 'moment'
import slugify from 'slugify'
import { twMerge } from 'tailwind-merge'
import { DateTimeFormatOptions, NumberFormatOptions } from 'use-intl'
import { v4 } from 'uuid'

// helped function to test suspense from time to time
export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const isValidNumber = (value: unknown): boolean => {
  return value != null && value !== '' && !isNaN(Number(value))
}

export const decodeHtmlEntities = (text: string): string => {
  if (typeof window === 'undefined') return text // Prevents crashing on SSR
  const doc = new window.DOMParser().parseFromString(text, 'text/html')
  return doc.body.textContent || ''
}

export const toSQLLog = (q: { toSQL: () => { sql: string; params: unknown[] } }): string => {
  const { sql, params } = q.toSQL()

  const formattedSQL = sql.replace(/\$(\d+)/g, (_match: string, p1: string): string => {
    const param = params[Number(p1) - 1]

    if (typeof param === 'string') {
      return `'${param.replace(/'/g, "''")}'`
    }

    if (param === null) return 'NULL'
    if (param === undefined) return 'DEFAULT'

    return String(param) // Ensure everything else is a string
  })

  return `SQL: ${formattedSQL}`
}

export const hasPermissionAdmin = (roles: UserRolesTypeValue[] | undefined | null): boolean => roles?.includes(UserRolesType.admin) || false
export const hasPermissionAccountant = (roles: UserRolesTypeValue[] | undefined | null): boolean =>
  hasPermissionAdmin(roles) || roles?.includes(UserRolesType.accountant) || false
export const hasPermissionMember = (roles: UserRolesTypeValue[] | undefined | null): boolean =>
  hasPermissionAccountant(roles) || roles?.includes(UserRolesType.member) || false
export const isAspirant = (user: { roles?: UserRolesTypeValue[] | null } | null | undefined): boolean =>
  user?.roles?.includes(UserRolesType.aspirant) || false

// GAV-Mindestlohn (Stand 2026): 27.62 CHF/h × 2370 h/Jahr = 65'470 CHF/Jahr,
// auf 13 Monate verteilt 5'036.15 CHF/Mt. Spec verwendet gerundete Schwellen.
export const GAV_MIN_MONTHLY_100 = 5000
export const GAV_MIN_YEARLY_100 = 65000

const scale = (threshold: number, workloadPercent: number) => (threshold * workloadPercent) / 100

export const isBelowGAVMonthly = (monthly: number, workloadPercent: number): boolean =>
  monthly > 0 && monthly < scale(GAV_MIN_MONTHLY_100, workloadPercent)

export const isBelowGAVYearly = (yearly: number, workloadPercent: number): boolean =>
  yearly > 0 && yearly < scale(GAV_MIN_YEARLY_100, workloadPercent)

export const correlationId = () => v4().replace(/-/g, '').slice(0, 10)

/*
 * Als einfache Merkregel und Berechnungshilfe gilt: Multiplizieren Sie den zu
 * rundenden Betrag mit 20, danach runden Sie das Ergebnis kaufmännisch auf 0
 * Kommastellen und dann dividieren Sie wieder durch 20!
 * 1,000 - 1,024 → Beispiel: 1,024*20 = 20,48 ≈ 20  20/20 = 1
 * 1,025 - 1,074 → Beispiel: 1,025*20 = 20,5 ≈ 21  21/20 = 1,05
 * 1,075 - 1,099 → Beispiel: 1,089*20 = 21,78 ≈ 22  22/20 = 1,10
 *
 * Quelle: https://www.kingbill.com/de/blog/die-schweizer-rappenrundung.html
 */
export function kaufmaennischrundenCH(value: number, scale: number = 2): number {
  const x20 = value * 20
  const x20RoundOne = Math.round(x20)
  return parseFloat((x20RoundOne / 20).toFixed(scale))
}

export async function readableStreamToBuffer(readableStream: ReadableStream<Uint8Array>): Promise<Buffer> {
  const reader = readableStream.getReader()
  const chunks: Uint8Array[] = []

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    if (value) chunks.push(value)
  }

  return Buffer.concat(chunks.map((chunk) => Buffer.from(chunk)))
}
export const userS3Basepath = (user: User): string => `users/${user.email.replaceAll('EXITED_', '')}`

export function getKtgTyp(user: User, salaryConf: UserSalaryAttributesType): KtgValue {
  const tage = Number(salaryConf.ktg)
  if (tage && (tage === 30 || tage === 60)) {
    switch (user.profile.gender) {
      case 'women':
        return `women${tage}`
      case 'men':
        return `men${tage}`
    }
  }
  throw new Error(`Invalid Ktg: '${user.profile.gender}${tage}', allowed values are: ${ktgType.enumValues}`)
}

export const findUserSalaryConfigurationForYear = (user: User, year: string): UserSalaryAttributesType | undefined =>
  user.salary.configurations.find((s) => s.year === year)

export const prefillSalaries = (gross: number, bonusGross?: number) => [...new Array(12).fill(gross), bonusGross || 0]

export const lohnausweisForSelectedYear = (user: User, year: string): string | undefined =>
  findUserSalaryConfigurationForYear(user, year)?.lohnausweis

export function toDate(date: string): Date {
  return moment(date).utc().toDate()
}

export function formatDate(date: Date, format?: string): string {
  return moment(date).format(format)
}

export const dateToFirstDayOfMonthDate = (d: Date): Date => {
  return moment.utc(d).startOf('month').startOf('day').toDate()
}

export const dateToLastDayOfMonthDate = (d: Date): Date => {
  return moment.utc(d).endOf('month').startOf('day').toDate()
}

export const toYearFromYearMonth = (yearMonth: string) => {
  const year = Number(yearMonth.split('-')[0])
  const currentYear = new Date().getFullYear()
  return Number.isFinite(year) && year > 0 ? year : currentYear
}

export const toYearMonth = (year: string, month: number) => `${year}-${month.toString().padStart(2, '0')}`

export const yearMonthToFirstDayOfMonthDate = (yearMonth: string): Date => {
  return moment.utc(yearMonth, 'YYYY-MM').startOf('month').startOf('day').toDate()
}

export const yearMonthToLastDayOfMonthDate = (yearMonth: string): Date => {
  return moment.utc(yearMonth, 'YYYY-MM').endOf('month').startOf('day').toDate()
}

export function yearMonthToDate(yearMonth: string): Date {
  const [year, month] = yearMonth.split('-')
  const monthNum = parseInt(month, 10)
  if (monthNum > 12) {
    return moment.utc(`${parseInt(year, 10)}-12-31`, 'YYYY-MM-DD').toDate()
  } else {
    return moment.utc(`${yearMonth}-20`, 'YYYY-MM-DD').toDate()
  }
}

export enum ApiVersionBexio {
  V2 = '2.0',
  V3 = '3.0',
  V4 = '4.0',
}

export const toDateWithTimestamp = (issued: Date) =>
  moment(issued)
    .set({
      hour: moment().hour(),
      minute: moment().minute(),
      second: moment().second(),
      millisecond: moment().millisecond(),
    })
    .toDate()

export const toDateWithoutTimestamp = (issued: Date) => moment(issued).toDate()

export const getBexioVersion = (url: string): ApiVersionBexio | undefined => {
  const match = url.match(/\/(\d+\.\d+)\//)
  if (!match) return undefined
  const version = match[1] as ApiVersionBexio
  return Object.values(ApiVersionBexio).includes(version) ? version : undefined
}

export const slugme = (input: string) =>
  slugify(input, {
    replacement: '_',
    remove: /[#?!]/g,
    lower: true,
    strict: true,
  })

export function upperFirst(str: string): string {
  return str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : ''
}

export function getNameInitials(firstname: string, lastname?: string): string {
  const firstInitial = firstname ? upperFirst(firstname[0]) : ''
  const lastInitial = lastname ? upperFirst(firstname[0]) : ''
  return `${firstInitial}${lastInitial}`
}

export function getAbbreviatedName(firstname: string, lastname?: string): string {
  if (!lastname) return firstname
  const lastInitial = lastname ? upperFirst(lastname[0]) + '.' : ''
  return `${firstname} ${lastInitial}`
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function extractAndTruncate(input: string, truncate?: number): string {
  const extracted = input.split('<')[0].trim()

  if (truncate && extracted.length > truncate) {
    return extracted.slice(0, truncate) + '...'
  }

  return extracted
}

export function formatSalaryDate(yearMonth: string): string {
  const [year, month] = yearMonth.split('-').map(Number)
  if (!year || !month) {
    throw new Error('Invalid yearMonth format. Expected format: YYYY-MM')
  }
  if (month <= 12) {
    const date = new Date(year, month - 1) // Month is 0-indexed
    return new Intl.DateTimeFormat('de-CH', { month: 'short', year: 'numeric' }).format(date)
  } else {
    return 'Bonus'
  }
}

/**
 * Date format options for Intl.DateTimeFormat.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#using_options
 */
export const dateformat: DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  weekday: 'short',
}

export const dateformatMobile: DateTimeFormatOptions = {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
}

/**
 * Date format options for Intl.DateTimeFormat.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat#using_options
 */
const currencyFormatters: Record<string, NumberFormatOptions> = {
  chf: {
    style: 'currency',
    currency: 'CHF',
    signDisplay: 'negative',
  },
}

export const getDecimalFormatter = (maximumFractionDigits: number = 2, minimumFractionDigits?: number) => {
  const fmt = new Intl.NumberFormat('de-CH', {
    style: 'decimal',
    maximumFractionDigits,
    minimumFractionDigits,
    signDisplay: 'negative',
  })
  // de-CH thousands separator varies by ICU version (Node emits U+2019, browser U+0027 or U+202F),
  // which breaks Next SSR hydration. Normalize to the straight apostrophe used in our translations.
  return { format: (n: number) => fmt.format(n).replace(/[’  ]/g, "'") }
}

// Swiss money format without currency symbol, e.g. 1234.5 → 1'234.50
export const formatCHMoney = (n: number): string => getDecimalFormatter(2, 2).format(n)

export const getCurrencyFormatter = (maximumFractionDigits: number = 2): Intl.NumberFormat => {
  const cur = (process.env.APP_DEFAULT_CURRENCY || 'CHF').toLowerCase()
  const f = currencyFormatters[cur] ? currencyFormatters[cur] : currencyFormatters.chf
  return new Intl.NumberFormat('de-CH', {
    ...f,
    maximumFractionDigits,
  })
}

/**
 * Date format options for Intl.DateTimeFormat.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 */
export const getDateFormatter = () => {
  return new Intl.DateTimeFormat('de-CH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export const getDateFormatterLong = () => {
  return new Intl.DateTimeFormat('de-CH', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  })
}

export const getDateFormatterYearMonth = () => {
  return new Intl.DateTimeFormat('de-CH', {
    year: 'numeric',
    month: 'long',
  })
}

export const getDateTimeFormatter = () => {
  return new Intl.DateTimeFormat('de-CH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export const formatAddress = (address: Contact) => {
  const { street, house_number, zip, city } = address
  const streetPart = [street, house_number].filter(Boolean).join(' ')
  const cityPart = [zip, city].filter(Boolean).join(' ')
  return [streetPart, cityPart].filter(Boolean).join(', ')
}

export const incomeTypesMapping = [
  {
    value: IncomeType.income_intern_invoice,
    icon: HouseIcon,
    color: 'bg-lime-100 dark:bg-lime-950',
  },
  {
    value: IncomeType.income_extern_invoice,
    icon: BackpackIcon,
    color: 'bg-amber-100 dark:bg-amber-950',
  },
  {
    value: IncomeType.income_extern_expense,
    icon: ReceiptSwissFrancIcon,
    color: 'bg-orange-100 dark:bg-orange-950',
  },
]

export const expenseTypesMapping = [
  {
    value: ExpenseType.expense_bill,
    icon: ReceiptTextIcon,
    color: 'bg-indigo-100 dark:bg-indigo-950',
  },
  {
    value: ExpenseType.expense_bill_intern,
    icon: Building2Icon,
    color: 'bg-amber-100 dark:bg-amber-950',
  },
  {
    value: ExpenseType.expense_bill_intern_flatrate,
    icon: ReceiptSwissFrancIcon,
    color: 'bg-teal-100 dark:bg-teal-950',
  },
  {
    value: ExpenseType.expense_training,
    icon: GraduationCapIcon,
    color: 'bg-orange-100 dark:bg-orange-950',
  },
  {
    value: ExpenseType.expense_business_meal,
    icon: SaladIcon,
    color: 'bg-lime-100 dark:bg-lime-950',
  },
]

export const isInterExpense = (type?: ExpenseTypeValue) =>
  type === ExpenseType.expense_bill_intern || type === ExpenseType.expense_bill_intern_flatrate

export const bookingStatuses = [
  {
    value: EntryStatusType.booked,
    icon: CheckCircle,
    color: 'bg-green-100 dark:bg-green-950',
  },
  {
    value: EntryStatusType.draft,
    icon: PencilIcon,
    color: 'bg-fuchsia-100 dark:bg-fuchsia-950',
  },
  {
    value: EntryStatusType.to_book,
    icon: Timer,
    color: 'bg-blue-100 dark:bg-blue-950',
  },
  {
    value: EntryStatusType.canceled,
    icon: CircleXIcon,
    color: 'bg-red-100 dark:bg-red-950',
  },
  {
    // Terminal: a member rejected an ingested Revolut draft as a personal charge — read-only, never booked.
    value: EntryStatusType.private_charge,
    icon: WalletMinimalIcon,
    color: 'bg-amber-100 dark:bg-amber-950',
  },
]

export const contactTypes = [
  {
    value: 'unknown',
    icon: OctagonAlertIcon,
    color: 'bg-gray-100 dark:bg-gray-950',
  },
  {
    value: 'company',
    icon: Building2Icon,
    color: 'bg-lime-100 dark:bg-lime-950',
  },
  {
    value: 'person',
    icon: UserRoundIcon,
    color: 'bg-amber-100 dark:bg-amber-950',
  },
]

export const bexioStatuses = [
  {
    value: 'unknown',
    icon: CircleHelpIcon,
    color: 'bg-gray-100 dark:bg-gray-950',
  },
  {
    value: 'draft',
    icon: PencilIcon,
    color: 'bg-gray-100 dark:bg-gray-950',
  },
  {
    value: 'pending',
    icon: Timer,
    color: 'bg-blue-100 dark:bg-blue-950',
  },
  {
    value: 'paid',
    icon: CheckCircle,
    color: 'bg-green-100 dark:bg-green-950',
  },
  {
    value: 'partial',
    icon: CircleDashedIcon,
    color: 'bg-gray-100 dark:bg-gray-950',
  },
  {
    value: 'canceled',
    icon: CircleXIcon,
    color: 'bg-red-100 dark:bg-red-950',
  },
  {
    value: 'unpaid',
    icon: Circle,
    color: 'bg-orange-100 dark:bg-orange-950',
  },
  {
    value: 'unknown',
    icon: OctagonAlertIcon,
    color: 'bg-red-100 dark:bg-red-950',
  },
  {
    value: 'booked',
    icon: CheckCircle,
    color: 'bg-green-100 dark:bg-green-950',
  },
  {
    value: 'partially_created',
    icon: PencilIcon,
    color: 'bg-gray-100 dark:bg-gray-950',
  },
  {
    value: 'created',
    icon: FilePlusIcon,
    color: 'bg-blue-100 dark:bg-blue-950',
  },
  {
    value: 'partially_sent',
    icon: CircleDashedIcon,
    color: 'bg-orange-100 dark:bg-orange-950',
  },
  {
    value: 'sent',
    icon: SendIcon,
    color: 'bg-blue-100 dark:bg-blue-950',
  },
  {
    value: 'partially_downloaded',
    icon: ArrowDownCircle,
    color: 'bg-yellow-100 dark:bg-yellow-950',
  },
  {
    value: 'downloaded',
    icon: ArrowDown,
    color: 'bg-green-100 dark:bg-green-950',
  },
  {
    value: 'partially_paid',
    icon: CircleDashedIcon,
    color: 'bg-green-100 dark:bg-green-950',
  },
  {
    value: 'partially_failed',
    icon: CircleDashedIcon,
    color: 'bg-red-100 dark:bg-red-950',
  },
  {
    value: 'failed',
    icon: CircleXIcon,
    color: 'bg-red-100 dark:bg-red-950',
  },
  {
    value: 'todo',
    icon: ClipboardList,
    color: 'bg-orange-100 dark:bg-orange-950',
  },
  {
    value: 'overdue',
    icon: AlertCircleIcon,
    color: 'bg-red-100 dark:bg-red-950',
  },
  {
    value: 'drafts',
    icon: PencilIcon,
    color: 'bg-gray-100 dark:bg-gray-950',
  },
]

export const relationTypes = [
  {
    value: 'contact_in_charge',
    icon: UserRoundIcon,
    color: 'bg-lime-100 dark:bg-lime-950',
  },
  {
    value: 'customer_of',
    icon: BanknoteArrowUpIcon,
    color: 'bg-green-100 dark:bg-green-950',
  },
  {
    value: 'supplier_to',
    icon: CarIcon,
    color: 'bg-blue-100 dark:bg-blue-950',
  },
  {
    value: 'partner',
    icon: UsersIcon,
    color: 'bg-amber-100 dark:bg-amber-950',
  },
  {
    value: 'subsidiary',
    icon: Building2Icon,
    color: 'bg-purple-100 dark:bg-purple-950',
  },
  {
    value: 'parent_company',
    icon: Building2Icon,
    color: 'bg-pink-100 dark:bg-pink-950',
  },
  {
    value: 'competitor',
    icon: OctagonAlertIcon,
    color: 'bg-red-100 dark:bg-red-950',
  },
]

export const noteTypes = [
  {
    value: 'general',
    icon: NotepadTextIcon,
    color: 'bg-gray-100 dark:bg-gray-950',
  },
  {
    value: 'call',
    icon: PhoneIcon,
    color: 'bg-blue-100 dark:bg-blue-950',
  },
  {
    value: 'meeting',
    icon: UsersIcon,
    color: 'bg-green-100 dark:bg-green-950',
  },
  {
    value: 'email',
    icon: MailIcon,
    color: 'bg-purple-100 dark:bg-purple-950',
  },
  {
    value: 'task',
    icon: ListTodoIcon,
    color: 'bg-orange-100 dark:bg-orange-950',
  },
]

export const contactStages = [
  {
    value: 'partner',
    icon: UserRoundIcon,
    color: 'bg-lime-100 dark:bg-lime-950',
  },
  {
    value: 'customer',
    icon: BanknoteArrowUpIcon,
    color: 'bg-green-100 dark:bg-green-950',
  },
  {
    value: 'member',
    icon: CircleStarIcon,
    color: 'bg-red-100 dark:bg-red-950',
  },
  {
    value: 'lead',
    icon: ArrowUpRightIcon,
    color: 'bg-yellow-100 dark:bg-yellow-950',
  },
  {
    value: 'supplier',
    icon: CarIcon,
    color: 'bg-blue-100 dark:bg-blue-950',
  },
  {
    value: 'inactive',
    icon: CircleXIcon,
    color: 'bg-gray-100 dark:bg-gray-950',
  },
]

export const bexioStatus2Color = (o: string) => {
  switch (o) {
    case 'draft':
      return 'gray'
    case 'confirmed':
      return 'green'
    case 'pending':
      return 'violet'
    case 'open':
      return 'blue'
    case 'paid':
      return 'lime'
    case 'done':
      return 'green'
    case 'partial':
      return 'yellow'
    case 'canceled':
      return 'red'
    case 'unpaid':
      return 'red'
    default:
      return 'gray'
  }
}
// https://docs.bexio.com/#tag/Bills/operation/ApiBillsList_GET
/*
* string
Enum: "DRAFTS" "TODO" "PAID" "OVERDUE"
Example: status=TODO
filter for Bill 'status' (DRAFTS: [DRAFT],
* TODO: [BOOKED, PARTIALLY_CREATED, CREATED, PARTIALLY_SENT, SENT, PARTIALLY_DOWNLOADED, DOWNLOADED,
*  PARTIALLY_PAID, PARTIALLY_FAILED, FAILED],
* PAID: [PAID],
* OVERDUE: [BOOKED, PARTIALLY_CREATED, CREATED, PARTIALLY_SENT, SENT, PARTIALLY_DOWNLOADED, DOWNLOADED, PARTIALLY_PAID, PARTIALLY_FAILED, FAILED])
* and for 'onlyOverdue' (DRAFTS: [FALSE], TODOS: [FALSE], PAID: [FALSE], OVERDUE: [TRUE]).
* Choosing OVERDUE means that only Bills with 'due_date' before now will be shown
* */
export const bexioStatus2Word = (o?: number | null) => {
  // data retrieved from bexio: https://docs.bexio.com/legacy/kb_item_status/index.html
  /*
  Invoices
  kb_item_status_id	Description
  7	Draft
  8	Pending
  9	Paid
  16	Partial
  19	Canceled
  31	Unpaid

  for orders:
kb_item_status_id: 21 => Storniert
kb_item_status_id: 15 => Teilweise
"kb_item_status_id": 5 => offen
"kb_item_status_id": 6, => erledigt
   */
  if (!o) return 'unknown'
  switch (o.toString()) {
    case '1':
      return 'draft'
    case '2':
      return 'open'
    case '3':
      return 'confirmed'
    case '7':
      return 'draft'
    case '8':
      return 'pending'
    case '9':
      return 'paid'
    case '16':
      return 'partial'
    case '19':
      return 'canceled'
    case '31':
      return 'unpaid'
    case '21':
      return 'canceled'
    case '15':
      return 'partial'
    case '5':
      return 'open'
    case '6':
      return 'done'
    default:
      return 'unknown'
  }
}

/**
 * // Example Usage:
 *
 * (calculateMonthsForYear(2023)); // [1, 2, ..., 12]
 * (calculateMonthsForYear(2024, 6)); // [1, 2, 3, 4, 5, 6]
 * (calculateMonthsForYear(new Date().getFullYear())); // [1, ..., current month]
 *
 * @param year
 * @param month
 */
export function calculateMonthsForYear(year: number, month?: number): number[] {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1 // Months are 0-indexed in JS

  // If month is explicitly provided, use it (highest priority)
  if (month !== undefined) {
    return Array.from({ length: month }, (_, i) => i + 1)
  }

  // For past years, include bonus month (13 months)
  if (year < currentYear) {
    return Array.from({ length: 13 }, (_, i) => i + 1)
  }

  // For current year without explicit month, use current month
  if (year === currentYear) {
    return Array.from({ length: currentMonth }, (_, i) => i + 1)
  }

  // For future years without explicit month, include bonus month (13 months)
  return Array.from({ length: 13 }, (_, i) => i + 1)
}

// a wrapper function for console.log, so the code should not contain any console.log and if we need console.log
// then use this wrapper, so we know its intentional!
export const consoleInfo = (...args: unknown[]): void => {
  console.log('[INFO]', ...args)
}

// a wrapper function for console.log, so the code should not contain any console.log and if we need console.log
// then use this wrapper, so we know its intentional!

export const consoleError = (...args: unknown[]): void => {
  console.error('[ERROR]', ...args)
}

/**
 * Merges URL with additional query parameters, properly handling existing query strings
 * @param url - Base URL which may already contain query parameters
 * @param additionalParams - URLSearchParams to merge with existing parameters
 * @returns Complete URL with merged query parameters
 */
export const mergeUrlWithParams = (url: string, additionalParams: URLSearchParams): string => {
  const urlObj = new URL(url, 'http://localhost') // Use dummy base for relative URLs
  additionalParams.forEach((value, key) => {
    if (key === 'year' || key === 'user') {
      // only allow year and user to be merged, remove the filters
      urlObj.searchParams.set(key, value)
    }
  })
  return `${urlObj.pathname}${urlObj.search}`
}

// Balancesheet
export const sumTotalExpenses = (data: Balancesheet[]): number => {
  return data
    .filter((x) => x.accountType === AccountType.main)
    .filter((x) => expenseType.enumValues.includes(x.type as ExpenseTypeValue))
    .reduce((acc, bs) => {
      return acc + bs.total
    }, 0)
}

// spesen
export const sumTotalPersonalExpenses = (data: Balancesheet[]): number => {
  return data
    .filter((x) => x.accountType === AccountType.expense)
    .reduce((acc, bs) => {
      return acc + bs.total
    }, 0)
}

export const sumTotalIncome = (data: Balancesheet[]): number => {
  return data
    .filter((x) => x.accountType === AccountType.main)
    .filter(
      (x) =>
        x.type === BookingType.income_extern_invoice ||
        x.type === BookingType.income_intern_invoice ||
        x.type === BookingType.income_extern_expense
    )
    .reduce((acc, bs) => {
      return acc + bs.total
    }, 0)
}

export const sumTotalDeposit = (data: Balancesheet[]): number => {
  return data
    .filter((x) => x.accountType === AccountType.deposit)
    .reduce((acc, bs) => {
      return acc + bs.total
    }, 0)
}

export const sumTotalCosts = (data: Balancesheet[]): number => {
  const expenses = [
    ...expenseType.enumValues,
    SalaryType.salary_net,
    SalaryType.salary_total_deductions_employee_part,
    SalaryType.salary_total_deductions_employer_part,
    IncomeType.expense_company_interest,
    TransferType.transfer_deposit,
    TransferType.transfer,
  ]
  return data
    .filter((x) => x.accountType === AccountType.main)
    .filter((x) => expenses.map((x) => x.toString()).includes(x.type.toString()))
    .reduce((acc, bs) => {
      return acc + bs.total
    }, 0)
}

export function calculateAge(birthday?: string): string {
  if (!birthday) return ''
  try {
    return moment().diff(moment(birthday, 'DD.MM.YYYY'), 'years').toString()
  } catch {
    return ''
  }
}

export function formatJobFromTo(date?: string): string {
  if (!date) return '-'
  try {
    return new Intl.DateTimeFormat('de-CH', { month: 'long', year: 'numeric' }).format(moment(date, 'DD.MM.YYYY').toDate())
  } catch {
    return date
  }
}

/**
 * Deep merges two objects recursively, with properties from source overriding target
 * Arrays are replaced entirely, not merged
 * @param target - Base object (default resume)
 * @param source - Override object (language-specific resume)
 * @returns Merged object
 */
function deepMerge<T extends Record<string, unknown>>(target: T, source: Partial<T>): T {
  if (!source || typeof source !== 'object') return target
  if (!target || typeof target !== 'object') return source as T

  const result = { ...target }

  for (const key in source) {
    const sourceValue = source[key]
    const targetValue = result[key]

    // Skip undefined values from source
    if (sourceValue === undefined) continue

    // If source value is null, override
    if (sourceValue === null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result[key] = sourceValue as any
      continue
    }

    // If both are arrays, replace entirely with source array
    if (Array.isArray(sourceValue)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result[key] = sourceValue as any
      continue
    }

    // If both are objects (and not arrays), recursively merge
    if (typeof sourceValue === 'object' && typeof targetValue === 'object' && !Array.isArray(targetValue)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result[key] = deepMerge(targetValue as Record<string, unknown>, sourceValue as Record<string, unknown>) as any
      continue
    }

    // Otherwise, just override
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result[key] = sourceValue as any
  }

  return result
}

/**
 * Gets a resume from user data with deep merge support for language variants
 * @param userResumes - The user.resumes JSONB field
 * @param lang - Language code (e.g., 'en', 'de')
 * @returns Merged resume or null if no data available
 */
const defaultLang = 'de'
export function getResumeFromUserData(userResumes: ResumesType, lang: string = defaultLang): ResumeSchemaType {
  const defaultResume = userResumes.resumes.find((r) => r.language === defaultLang)?.resume || {}

  if (lang === defaultLang) return defaultResume

  const langResume = userResumes.resumes.find((r) => r.language === lang)?.resume
  if (!langResume) return defaultResume

  return deepMerge(defaultResume, langResume)
}

/**
 * Initializes a resume from template with user data
 * Replaces ${user.field} and ${user.profile.field} placeholders with actual values
 * @param user - User object with firstname, lastname, email, and profile
 * @returns Initialized ResumesType object
 */
export function initializeResumeFromTemplate(user: User): ResumesType {
  // Read template from public/resumes/resume-tpl.json
  let templateStr = `{
  "resumes": [
    {
      "language": "de",
      "resume": {
        "basics": {
          "name": "\${user.firstname} \${user.lastname}",
          "email": "\${user.email}",
          "image": "\${user.image}",
          "label": "Schaltstelle Member",
          "summary": "Baut Apps, fixt Bugs und zerlegt gelegentlich das Raum-Zeit-Kontinuum – alles für saubereren Code.",
          "gender": "\${user.gender}",
          "birth": {
            "date": "\${user.birthdate}"
          },
          "location": {
            "address": "Breitenrainplatz 40",
            "postalCode": "3014",
            "city": "Bern",
            "countryCode": "CH",
            "region": "Schweiz"
          },
          "profiles": [
            {
              "network": "GitHub",
              "username": "\${user.github}",
              "url": "\${user.github}"
            },
            {
              "network": "LinkedIn",
              "username": "\${user.linkedin}",
              "url": "\${user.linkedin}"
            }
          ]
        },
        "work": [
          {
            "name": "PortalFlux Systems",
            "position": "CTO & Mitgründer",
            "startDate": "01.02.2021",
            "summary": "Verantwortlich für alles Technische – von der Architektur bis zum letzten CI/CD-Knopf. Baute eine Plattform, die sich anfühlt wie eine Portal-Gun für APIs.",
            "location": {
              "region": "Remote / Multiversum"
            },
            "highlights": [
              "Bau einer stabilen Cloud-Architektur (stabiler als Mortys Nerven)",
              "Einführung von CI/CD, damit Deployments nicht wie ein fehlerhafter Portal-Sprung enden",
              "Leitung eines interdimensionalen Entwicklerteams",
              "Technischer Ansprechpartner für Kunden – von ‘Hello World’ bis ‘Warum brennt’s?’",
              "Enge Zusammenarbeit mit Produktleuten, die zu viele Features wollen",
              "Codequalität gesichert durch Tests, Reviews und gelegentliche sarkastische Kommentare"
            ]
          }
        ],
        "projects": [
          {
            "name": "Schaltzentrale App",
            "startDate": "01.10.2024",
            "endDate": "01.04.2025",
            "description": "Bau einer kleinen internen Buchhaltungs-App, die endlich versteht, was KMUs wirklich brauchen – und die nicht abstürzt, sobald jemand eine Spesenquittung hochlädt.",
            "highlights": [
              "Frontend mit Next.js & TypeScript (schöner, schneller, weniger Morty-Schreie)",
              "Responsives UI für Handy, Desktop und interdimensionale Bildschirme",
              "PostgreSQL-Backend in einer Cloud, die hoffentlich nicht brennt",
              "CI/CD-Automatisierung, damit Deployments nicht nach Portal-Experimenten aussehen",
              "Anbindung an externe Buchhaltungs-Services",
              "Agile Sprints im Zweiwochentakt, meistens mit Kaffee, selten mit Panik"
            ]
          }
        ],
        "education": [
          {
            "institution": "Galaktische Hochschule für Angewandte Informatik",
            "area": "Interdimensionales Software Engineering",
            "studyType": "Diplom-Informatiker",
            "startDate": "01.06.2003",
            "endDate": "01.06.2007",
            "summary": "Studierte alles von Software-Architektur bis Datenbanken, inklusive dem geheimen Fach: ‘Wie verhindere ich Portal-bedingte Datenverluste?’. Abschluss mit sehr gut – angeblich ohne Zeitreise-Tricks.",
            "courses": [
              "Software Engineering in mehreren Dimensionen",
              "Datenbanken & verteilte Systeme quer durch die Galaxie",
              "IT-Sicherheit & Kryptographie (keine Portal-Schlüssel verloren!)",
              "Projektmanagement mit chaotischen Stakeholdern",
              "Java, C/C++, Webentwicklung & ein Hauch von Laserphysik"
            ]
          }
        ],
        "skills": [
          {
            "name": "Tech",
            "level": "Experte",
            "keywords": [
              "Java",
              "Kotlin",
              "React",
              "Node.js",
              "PostgreSQL",
              "MongoDB",
              "API-Kram"
            ]
          },
          {
            "name": "DevOps",
            "level": "Fortgeschritten",
            "keywords": [
              "AWS",
              "CI/CD",
              "GitHub Actions",
              "GitLab Pipelines",
              "Cypress",
              "Jenkins"
            ]
          },
          {
            "name": "Backend-Magie",
            "level": "Solide",
            "keywords": [
              "Python",
              "Go",
              "Scala",
              "AngularJS/Angular"
            ]
          }
        ],
        "languages": [
          {
            "language": "Deutsch",
            "fluency": "Muttersprache"
          },
          {
            "language": "English",
            "fluency": "C1"
          }
        ],
        "citizenship": [
          "Schweiz"
        ],
        "certificates": [
          {
            "name": "Certified Java Developer",
            "issuer": "Sun Microsystems"
          },
          {
            "name": "Certified Scala Developer",
            "issuer": "Coursera / Martin Odersky"
          },
          {
            "name": "Certified iSAQB Software Architect",
            "issuer": "Gernot Starke"
          }
        ]
      }
    }
  ]
}`

  // Replace placeholders with actual user data
  templateStr = templateStr.replace(/\$\{user\.firstname}/g, user.firstname)
  templateStr = templateStr.replace(/\$\{user\.lastname}/g, user.lastname)
  templateStr = templateStr.replace(/\$\{user\.email}/g, user.email)
  templateStr = templateStr.replace(/\$\{user\.github}/g, (user.profile.github as string) || '')
  templateStr = templateStr.replace(/\$\{user\.linkedin}/g, (user.profile.linkedin as string) || '')
  templateStr = templateStr.replace(/\$\{user\.image}/g, toUserProfileImageLocalFilename(user))

  // Gender: map 'women'/'men'/'other' to first character 'w'/'m'/'o'
  const gender = user.profile.gender as string | undefined
  const genderChar = gender ? gender.charAt(0) : ''
  templateStr = templateStr.replace(/\$\{user\.gender}/g, genderChar)

  // Birthdate: format as DD.MM.YYYY
  const birthday = user.profile.birthday as Date | string | undefined
  let birthdayFormatted = ''
  if (birthday) {
    try {
      const birthDate = birthday instanceof Date ? birthday : new Date(birthday)
      birthdayFormatted = moment(birthDate).format('DD.MM.YYYY')
    } catch {
      birthdayFormatted = birthday.toString()
    }
  }
  templateStr = templateStr.replace(/\$\{user\.birthdate}/g, birthdayFormatted)

  return JSON.parse(templateStr) as ResumesType
}

export const toUserProfileImageLocalFilename = (user: User): string => {
  return `/profile-images/${normalize(user.firstname)}${normalize(user.lastname)}.webp`.toLowerCase()
}

const normalize = (str: string) =>
  str
    .normalize('NFD') // split accents from letters
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .replace(/[^a-zA-Z0-9]/g, '') // remove special characters

export const isTestOrDev = process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development'
